/**
 * Ruta en vivo del viaje (Fase 2): solo la app del conductor llama a Google Directions y guarda la
 * ruta en booking_routes/{bookingId}; el pasajero la lee de ahí. El tiempo y la distancia restantes
 * se calculan en el teléfono sobre la polilínea guardada.
 */
import { GetDistance } from './GeoFunctions';

export const ROUTE_DEVIATION_KM = 0.1;
/** Intervalo mínimo entre consultas “normales” / sucesivas por desvío (coste). */
export const ROUTE_MIN_INTERVAL_MS = 2 * 60 * 1000;
/** Primer recálculo tras salir de ruta: más ágil (premium), sin martillar Google. */
export const ROUTE_DEVIATE_FIRST_MS = 20 * 1000;
/** Recálculos siguientes mientras sigue fuera de ruta. */
export const ROUTE_DEVIATE_NEXT_MS = 90 * 1000;
/** Tope de recálculos por desvío por tramo (pickup / drop / return). */
export const ROUTE_MAX_DEVIATION_RECALCS = 6;
/**
 * Atasco: siguiendo la ruta pero sin avanzar ROUTE_STALL_PROGRESS_KM en ROUTE_STALL_MS, se vuelve a pedir
 * la ruta con el tráfico actual para que el ETA suba en vez de quedarse congelado.
 */
export const ROUTE_STALL_MS = 150 * 1000;
export const ROUTE_STALL_PROGRESS_KM = 0.2;
/** Ya junto al destino (esperando al pasajero, buscando dónde parar): no es un atasco. */
export const ROUTE_STALL_MIN_REMAINING_KM = 0.4;
export const ROUTE_MAX_STALL_RECALCS = 4;
export const ROUTE_RETRY_MS = 30 * 1000;
/** Pasajero con conductor de una versión vieja (no guarda la ruta): espera y luego consulta él mismo. */
export const FALLBACK_WAIT_MS = 20 * 1000;

/** Espera para volver a pedir la ruta del tramo: 30 s tras el primer intento fallido, luego 2 min. */
export const routeRetryDelay = (failures = 0) => (failures > 1 ? ROUTE_MIN_INTERVAL_MS : ROUTE_RETRY_MS);

/**
 * Polilínea de calle (Directions): muchos vértices.
 * Recta A→B o solo pickup→paradas→drop: pocos puntos — no se pinta (estilo Uber/inDrive).
 */
export const MIN_ROAD_POLYLINE_POINTS = 8;

export function isRoadPolyline(coords) {
  if (!Array.isArray(coords) || coords.length < MIN_ROAD_POLYLINE_POINTS) return false;
  let valid = 0;
  for (let i = 0; i < coords.length; i++) {
    const p = coords[i];
    const lat = p && (p.latitude != null ? p.latitude : p.lat);
    const lng = p && (p.longitude != null ? p.longitude : p.lng);
    if (lat != null && lng != null && !isNaN(Number(lat)) && !isNaN(Number(lng))) valid += 1;
  }
  return valid >= MIN_ROAD_POLYLINE_POINTS;
}

/** ¿El GPS está a más de `thresholdKm` de la polilínea? */
export function isOffRoute(position, coords, thresholdKm = ROUTE_DEVIATION_KM) {
  if (!position || !Array.isArray(coords) || coords.length < 2) return false;
  return distanceToRouteKm(position, coords) > thresholdKm;
}

/**
 * ETA suavizado (EMA): baja más rápido al acercarse; sube con suavidad (evita saltos al salir de ruta).
 * @returns {number|null} minutos enteros ≥ 1
 */
export function smoothEtaMinutes(prevMinutes, nextMinutes, alpha = 0.35) {
  if (nextMinutes == null || !Number.isFinite(Number(nextMinutes))) return prevMinutes ?? null;
  const next = Math.max(1, Number(nextMinutes));
  if (prevMinutes == null || !Number.isFinite(Number(prevMinutes))) return next;
  const prev = Number(prevMinutes);
  if (next < prev) {
    return Math.max(1, Math.round(prev * 0.4 + next * 0.6));
  }
  return Math.max(1, Math.round(prev * (1 - alpha) + next * alpha));
}

/** Estados en que la app del conductor guarda tracking/{bookingId} (camino al pasajero y viaje). */
export const TRACKING_STATUSES = ['ACCEPTED', 'PICKUP_USER', 'STARTED'];
export const isTripTrackingStatus = (status) => TRACKING_STATUSES.indexOf(status) !== -1;

/** /settings/liveRouteTraffic: tiempo de la ruta con el tráfico actual; activo salvo que el admin lo apague. */
export const liveRouteTraffic = (settings) => !settings || settings.liveRouteTraffic !== false;

/**
 * Tramo de navegación en vivo.
 * Ida y vuelta: tras completar la ida (roundTripLeg=return) el destino es el origen.
 */
export const legForStatus = (status, booking) => {
  if (status === 'ACCEPTED' || status === 'PICKUP_USER') return 'pickup';
  if (status === 'STARTED') {
    if (booking && (booking.roundTrip === true || booking.roundTrip === 1 || booking.roundTrip === '1')
        && booking.roundTripLeg === 'return') {
      return 'return';
    }
    return 'drop';
  }
  return null;
};

/** Polilínea codificada de Google -> [[lat, lng], ...] */
export function decodePolyline(encoded) {
  const points = [];
  if (typeof encoded !== 'string' || !encoded) return points;
  let index = 0;
  let lat = 0;
  let lng = 0;
  while (index < encoded.length) {
    let result = 0;
    let shift = 0;
    let byte;
    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20 && index < encoded.length);
    lat += result & 1 ? ~(result >> 1) : result >> 1;
    result = 0;
    shift = 0;
    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20 && index < encoded.length);
    lng += result & 1 ? ~(result >> 1) : result >> 1;
    points.push([lat / 1e5, lng / 1e5]);
  }
  return points;
}

const KM_PER_DEG_LAT = 111.32;

/** Punto más cercano de la polilínea: segmento, fracción dentro del segmento y distancia en km. */
function nearestOnRoute(position, coords) {
  const lat0 = Number(position.lat);
  const lng0 = Number(position.lng);
  const kx = KM_PER_DEG_LAT * Math.cos((lat0 * Math.PI) / 180);
  const toXY = ([lat, lng]) => [(lng - lng0) * kx, (lat - lat0) * KM_PER_DEG_LAT];
  let best = { index: 0, t: 0, distanceKm: Infinity };
  if (coords.length === 1) {
    const [x, y] = toXY(coords[0]);
    return { index: 0, t: 0, distanceKm: Math.hypot(x, y) };
  }
  for (let i = 0; i < coords.length - 1; i++) {
    const [ax, ay] = toXY(coords[i]);
    const [bx, by] = toXY(coords[i + 1]);
    const dx = bx - ax;
    const dy = by - ay;
    const len2 = dx * dx + dy * dy;
    const t = len2 > 0 ? Math.max(0, Math.min(1, -(ax * dx + ay * dy) / len2)) : 0;
    const d = Math.hypot(ax + t * dx, ay + t * dy);
    if (d < best.distanceKm) best = { index: i, t, distanceKm: d };
  }
  return best;
}

const segmentKm = (a, b) => GetDistance(a[0], a[1], b[0], b[1]);

export function distanceToRouteKm(position, coords) {
  if (!position || !Array.isArray(coords) || coords.length === 0) return Infinity;
  return nearestOnRoute(position, coords).distanceKm;
}

/** Distancia por recorrer: hasta la polilínea y de ahí por la ruta hasta el final. */
export function remainingRouteKm(position, coords) {
  if (!position || !Array.isArray(coords) || coords.length === 0) return null;
  const { index, t, distanceKm } = nearestOnRoute(position, coords);
  if (coords.length === 1) return distanceKm;
  let rest = segmentKm(coords[index], coords[index + 1]) * (1 - t);
  for (let i = index + 1; i < coords.length - 1; i++) rest += segmentKm(coords[i], coords[i + 1]);
  return distanceKm + rest;
}

/** { distanceKm, minutes } restantes con la velocidad que estimó Google para esa ruta. */
export function estimateRemaining(route, coords, position) {
  const remainingKm = remainingRouteKm(position, coords);
  if (remainingKm === null) return null;
  const totalKm = Number(route?.distanceKm) || 0;
  const totalSecs = Number(route?.timeSecs) || 0;
  const secsPerKm = totalKm > 0 && totalSecs > 0 ? totalSecs / totalKm : 120;
  return { distanceKm: remainingKm, minutes: Math.max(1, Math.round((remainingKm * secsPerKm) / 60)) };
}

/**
 * Último punto en que el conductor avanzó por la ruta: { km, at, routeAt }.
 * Se reinicia con cada ruta nueva y cada vez que los km restantes bajan ROUTE_STALL_PROGRESS_KM.
 */
export function trackRouteProgress(progress, route, remainingKm, now) {
  if (remainingKm == null || !Number.isFinite(Number(remainingKm))) return progress || null;
  const routeAt = Number(route && route.at) || 0;
  if (!progress || progress.routeAt !== routeAt || remainingKm <= progress.km - ROUTE_STALL_PROGRESS_KM) {
    return { km: Number(remainingKm), at: now, routeAt };
  }
  return progress;
}

export function isRouteStalled(progress, remainingKm, now) {
  if (!progress || remainingKm == null) return false;
  if (remainingKm <= ROUTE_STALL_MIN_REMAINING_KM) return false;
  return now - progress.at >= ROUTE_STALL_MS;
}

/**
 * App del conductor: motivo para pedir una ruta nueva a Google, o null.
 * - 'leg': no hay ruta para el tramo actual (aceptó o inició el viaje).
 * - 'deviated': >100 m de la ruta; 1.er recálculo ~20 s, luego ~90 s; máx. ROUTE_MAX_DEVIATION_RECALCS por tramo.
 * - 'stalled': en la ruta pero sin avanzar (atasco); máx. ROUTE_MAX_STALL_RECALCS por tramo.
 */
export function shouldRecalculateRoute({
  route,
  coords,
  leg,
  position,
  now,
  lastRequestAt = 0,
  failures = 0,
  deviationRecalcs = 0,
  progress = null,
  remainingKm = null,
  stallRecalcs = 0,
}) {
  if (!leg || !position) return null;
  const sinceRequest = now - lastRequestAt;
  if (!route || route.leg !== leg) return sinceRequest >= routeRetryDelay(failures) ? 'leg' : null;
  if (isOffRoute(position, coords)) {
    if (deviationRecalcs >= ROUTE_MAX_DEVIATION_RECALCS) return null;
    const minWait = deviationRecalcs === 0 ? ROUTE_DEVIATE_FIRST_MS : ROUTE_DEVIATE_NEXT_MS;
    if (sinceRequest < minWait) return null;
    if (now - (Number(route.at) || 0) < minWait) return null;
    return 'deviated';
  }
  if (stallRecalcs >= ROUTE_MAX_STALL_RECALCS) return null;
  if (sinceRequest < ROUTE_STALL_MS) return null;
  return isRouteStalled(progress, remainingKm, now) ? 'stalled' : null;
}

/** App del pasajero sin ruta del conductor para el tramo: consulta propia, como máximo cada 2 min. */
export function shouldRequestFallbackRoute({ localRoute, leg, now, legSince, lastRequestAt = 0, failures = 0 }) {
  if (!leg || now - legSince < FALLBACK_WAIT_MS) return false;
  if (!localRoute || localRoute.leg !== leg) return now - lastRequestAt >= routeRetryDelay(failures);
  return now - lastRequestAt >= ROUTE_MIN_INTERVAL_MS;
}

/** Lo que se guarda en booking_routes/{bookingId}. */
export function buildRouteRecord({ leg, status, details, origin, now }) {
  if (!details || typeof details.polylinePoints !== 'string' || !details.polylinePoints) return null;
  return {
    leg,
    status,
    polyline: details.polylinePoints,
    distanceKm: Number(details.distance_in_km) || 0,
    timeSecs: Number(details.time_in_secs) || 0,
    origin: { lat: Number(origin.lat), lng: Number(origin.lng) },
    at: now,
  };
}
